import { Outlet } from 'react-router-dom';

import { AppShell } from '@/components/shell';
import { VerifyEmailGate } from '@/features/auth/VerifyEmailGate';
import { UnreadBadge } from '@/features/messages/UnreadBadge';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { NotificationCountBadge } from '@/features/notifications/NotificationCountBadge';
import {
  ProfileCompletionBadge,
  ProfileCompletionBanner,
} from '@/features/student/ProfileCompletionNudge';
import { useAuth } from '@/hooks/useAuth';
import { paths } from '@/routes/paths';

/**
 * The student's menu, in the sections of the product brief, ordered by what a
 * student opens most: dashboard, internships, AI features, notifications,
 * referral and subscription, with the profile sections last.
 *
 * One entry per destination, filed where the brief first names it, so no page
 * is reachable from two places and lit twice. "My internships" carries its
 * tabs as sub-items; "For you" is the Recommendations page under AI
 * features, because the brief lists recommendations there.
 */
const NAV = [
  {
    label: 'Dashboard & analytics',
    items: [
      { to: paths.student.dashboard, label: 'Dashboard', icon: 'dashboard' },
      { to: paths.student.insights, label: 'Application analytics', icon: 'trendingUp' },
    ],
  },
  {
    label: 'Internship management',
    items: [
      { to: paths.internships, label: 'Find internships', icon: 'search' },
      {
        to: paths.student.internships,
        label: 'My internships',
        icon: 'bookmark',
        children: [
          // The page's tabs, in the page's order, so My internships stays lit
          // on whichever tab is open. For you is now Recommendations below.
          // { to: paths.student.recommendations, label: 'For you', icon: 'sparkles' },
          { to: paths.student.applications, label: 'Applied', icon: 'clipboard' },
          { to: paths.student.saved, label: 'Saved', icon: 'bookmark' },
        ],
      },
      { to: paths.student.interviews, label: 'Interviews', icon: 'calendarClock' },
      { to: paths.student.offers, label: 'Offers', icon: 'contract' },
      {
        to: paths.student.messages,
        label: 'Messages',
        icon: 'chat',
        suffix: <UnreadBadge />,
      },
    ],
  },
  {
    label: 'AI features',
    items: [
      { to: paths.student.recommendations, label: 'Recommendations', icon: 'sparkles' },
      // Upload lives here too: the review, suggestions and match score all
      // start from the uploaded file.
      { to: paths.student.cv, label: 'CV & analysis', icon: 'fileText' },
      // Skill gap, career guidance and the step-by-step roadmap.
      { to: paths.student.career, label: 'Career guidance', icon: 'route' },
    ],
  },
  {
    label: 'Notifications',
    items: [
      {
        to: paths.notifications,
        label: 'Notifications',
        icon: 'bell',
        suffix: <NotificationCountBadge />,
      },
    ],
  },
  {
    label: 'Social & referral',
    items: [{ to: paths.student.invite, label: 'Invite classmates', icon: 'users' }],
  },
  {
    label: 'Subscription & payments',
    items: [{ to: paths.student.plan, label: 'Plans & billing', icon: 'creditCard' }],
  },
  {
    label: 'Account management',
    items: [
      // Personal information, picture, portfolio links, password, closing.
      {
        to: paths.student.profile,
        label: 'Profile & account',
        icon: 'user',
        suffix: <ProfileCompletionBadge />,
      },
      { to: paths.student.education, label: 'Education', icon: 'graduationCap' },
      { to: paths.student.experience, label: 'Work experience', icon: 'briefcase' },
      { to: paths.student.skills, label: 'Skills & certifications', icon: 'zap' },
    ],
  },
];

/** Signed-in student shell. */
export function StudentLayout() {
  const { user, signOut } = useAuth();

  return (
    <AppShell
      nav={NAV}
      sectionLabel="Student"
      roleLabel="Student"
      user={user}
      profileTo={paths.student.profile}
      accountMenu={[
        { to: paths.student.profile, label: 'My profile', icon: 'user' },
        { to: `${paths.student.profile}#account`, label: 'Change password', icon: 'shieldCheck' },
        { to: paths.student.plan, label: 'My plan', icon: 'creditCard' },
        { to: paths.notifications, label: 'Notifications', icon: 'bell' },
      ]}
      onSignOut={() => void signOut()}
      headerRight={<NotificationBell />}
      banner={<ProfileCompletionBanner />}
    >
      <VerifyEmailGate>
        <Outlet />
      </VerifyEmailGate>
    </AppShell>
  );
}
